import { useCallback, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CreateViewFromViewObjectModal } from 'customer-data-views-management/createView/components/CreateViewModal';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { useViewUpdateState } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useViewForm from '../hooks/useViewForm';
import { convertCustomerDataViewToView } from '../utils/mapper';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import useApplicationId from '../../hooks/useApplicationId';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { jsx as _jsx } from "react/jsx-runtime";
export default function CreateViewModal({
  formType,
  namespace,
  objectTypeId,
  onClose,
  onCreate
}) {
  const applicationId = useApplicationId();
  const buildViewClone = useViewForm();
  const [, setViewUpdateState] = useViewUpdateState();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const handleSuccess = useCallback(({
    createdView
  }) => {
    var _view$state$mLangGrou, _view$state;
    const view = convertCustomerDataViewToView(createdView);
    Metrics.counter(formType === 'CLONE_WITH_CHANGES' ? 'clone-view-with-changes-success' : 'create-new-view-success-v4', {
      application_id: applicationId
    }).increment();
    if (formType === 'CREATE_NEW') {
      trackCrmTableInteraction({
        action: 'create-view'
      });
    }
    setViewUpdateState({
      changedCardProperties: false,
      changedColumns: false,
      changedSort: false,
      changedUIMode: false,
      changedQuickFilterPropertyNames: false,
      mLangGroup: {
        changed: false,
        showAll: (_view$state$mLangGrou = (_view$state = view.state) === null || _view$state === void 0 || (_view$state = _view$state.mLangGroup) === null || _view$state === void 0 ? void 0 : _view$state.showAll) !== null && _view$state$mLangGrou !== void 0 ? _view$state$mLangGrou : false
      }
    });

    /**
     * Scroll table back to 0,0 to start fresh with the newly created view.
     */
    const frameworkDataTable = document.querySelector('[data-test-id="framework-data-table"]');
    if (frameworkDataTable && frameworkDataTable.parentElement) {
      frameworkDataTable.parentElement.scrollTo(0, 0);
    }
    alertSuccess({
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.success.message",
        options: {
          name: createdView.name
        }
      })
    });
    onCreate(view.id);
  }, [applicationId, formType, onCreate, setViewUpdateState, trackCrmTableInteraction]);
  const handleFailure = useCallback(() => {
    Metrics.counter(formType === 'CLONE_WITH_CHANGES' ? 'clone-view-with-changes-failure' : 'create-new-view-failure-v4', {
      application_id: applicationId
    }).increment();
    alertFailure({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.failure.message"
      }),
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkListingLib.viewTabs.alerts.viewCreate.failure.title"
      })
    });
  }, [applicationId, formType]);
  const viewObject = useMemo(() => {
    const viewClone = buildViewClone(formType);
    return {
      name: viewClone.name,
      objectTypeId,
      private: viewClone.private,
      namespaceName: namespace,
      columns: viewClone.columns,
      // Different TS types between FLL and customer-data-views-management
      state: viewClone.state,
      type: viewClone.type,
      filterGroups: viewClone.filterGroups,
      quickFilters: viewClone.quickFilters,
      quickFilterProperties: viewClone.quickFilterProperties
    };
  }, [formType, buildViewClone, namespace, objectTypeId]);
  return /*#__PURE__*/_jsx(CreateViewFromViewObjectModal, {
    viewObject: viewObject,
    strategy: formType === 'CLONE_WITH_CHANGES' ? 'CLONE_EXISTING' : 'CREATE_NEW',
    onSuccess: handleSuccess,
    onFailure: handleFailure,
    onClose: onClose
  });
}